import {
	Component, OnInit, OnChanges, SimpleChanges, ViewChild,
	Input, Output, ViewEncapsulation, EventEmitter } from '@angular/core';
import { language, notification, FormCollectorDirective } from 'curvy';
import { Task } from '@task-utils/types';
import { User } from '@task-modules/users/users';
import { users_translations } from '@task-modules/users/users.trans';
import UT = Task.USER_TYPE;
import { TaskRoutes } from '@task-utils/routes';

type UserType = Pick<Task.User, 'first_name' | 'last_name' | 'email' | 'contact_phone' | 'is_student' | 'institution' | 'hr_note'> & {company_id?: number};

@Component({
	selector: 'task-create-edit-external-worker',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="flex-column fullheight-noscroll">
			<h2 class="modal-title">
				{{ (is_edit_form ? 'USER.EDIT' : 'USER.NEW') | trans }}
			</h2>

			<div style="overflow: auto;">
				<div class="field-grid" form-collector #externalWorkerForm="collector">
					<label class="input outlined" [title]="'NAME' | trans">
						<input [(ngModel)]="user.first_name"
							conditions="nonempty"
							[disabled]="!can_edit" />
					</label>
					<label class="input outlined" [title]="'SURNAME' | trans">
						<input [(ngModel)]="user.last_name"
							conditions="nonempty"
							[disabled]="!can_edit" />
					</label>
					
					@if (!is_edit_form && is_sys_admin) {
						<knw-auto-search
							[required]="true"
							[function]="search_companies"
							[to-label]="format_company_name"
							label="{{'COMPANY' | trans }}"
							[(ngModel)]="picked_company"
							[popover-width]="'auto'"
							[classes]="'span-cols-full outlined mx-0'"
							(ngModelChange)="company_picker_change()"
						></knw-auto-search>
					}
					
					<label class="input outlined"
							[title]="'EMAIL' | trans"
							style="grid-column: 1 / -1" >
						<i icon="email"></i>
						<input [(ngModel)]="user.email"
								autocomplete="off"
								conditions="nonempty,pattern=.+@.+"
								[disabled]="!can_edit"/>
					</label>
					<label class="input outlined"
							[title]="'PHONE' | trans"
							style="grid-column: 1 / -1" >
						<i icon="phone"></i>
						<input [(ngModel)]="user.contact_phone"
								autocomplete="off"
								[disabled]="!can_edit"/>
					</label>

					<label class="checkbox-input"
							style="grid-column: 1 / -1" >
						<input 
							type="checkbox"
							[(ngModel)]="user.is_student"
							autocomplete="off"
							[disabled]="!can_edit"/>

						<span>{{ 'STUDENT' | trans }}</span>
					</label>
					
					<label class="input outlined"
						[title]="'INSTITUTION' | trans"
						style="grid-column: 1 / -1" >
						<input [(ngModel)]="user.institution"
								autocomplete="off"
								[disabled]="!can_edit"/>
					</label>

					<label class="input outlined whole-row" [title]="'DESCRIPTION' | trans">
						<textarea [(ngModel)]="user.hr_note" rows="3"></textarea>
					</label>

					<div *ngIf="show_password_email_button && is_edit_form"
							class="flex-row justify-flex-end"
							style="grid-column: 1 / -1; margin-bottom: 9px">
						<button class="pill"
								(click)="send_password_email()">
							<i icon="email"></i>
							<span>{{ 'USER.SEND_PASSWORD_EMAIL' | trans }}</span>
						</button>
					</div>
				</div>
			</div>

			<div class="flex-row modal-footer">
				<button color="primary"
						(click)="cancel()">
					<i icon="clear"></i>
					<span>{{ 'CANCEL' | trans }}</span>
				</button>

				<button class="filled"
						color="secondary"
						(click)="done()"
						[disabled]="externalWorkerForm.containsErrors()">
					<i icon="check"></i>
					<span>{{ 'SAVE' | trans }}</span>
				</button>

			</div>
		</div>
	`,
	styles: [`
		task-create-edit-external-worker {
			max-width: 600px;
		}

		.span-cols-full {
			grid-column: 1 / -1;
		}

		.mx-0 {
			margin-left: 0px!important;
			margin-right: 0px!important;
		}

		task-create-edit-external-worker knw-search-picker {
			display: contents;
		}

		task-create-edit-external-worker knw-search-picker label[title="Location"] {
			flex-flow: row-reverse;
		}

		task-create-edit-external-worker knw-search-picker label[title="Location"] input {
			margin-right: 0;
			margin-left: 24px;
		}

		task-create-edit-external-worker knw-search-picker label[title="Location"]::after {
			content: "place";
			font-family: Icons;
			font-size: 24px;
			line-height: 0px;
			margin-right: -24px;
		}

		.error-message {
			color: red;
			padding: 1em;
		}
	`]
})
export class CreateEditExternalWorkerComponent implements OnInit, OnChanges {
	@ViewChild('externalWorkerForm')
	externalWorkerForm: FormCollectorDirective;

	@Input()
	user: UserType = {
		first_name: "",
		last_name: "",
		email: "",
		contact_phone: "",
		is_student: false,
		institution: "",
		hr_note: ""
	};

	@Output('result')
	result: EventEmitter<Task.User | UserType> = new EventEmitter();

	show_password_email_button = true;
	is_edit_form = false;

	can_edit = false;
	is_sys_admin = false;

	picked_company: Task.Company = null;




	ngOnInit() {
		this.init();
	}

	async init() {
		language.load_translations(users_translations);
		

		this.update_details();

	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes["user"]) {
			this.user = {...changes["user"].currentValue};
		}

		this.update_details();
	}

	company_picker_change() {
		if (this.picked_company) {
			this.user.company_id = this.picked_company.company_id
		}

	}

	
	async search_companies(search: string) {
		let res = await TaskRoutes.companies.api_company_search(
			{ search: [search], attributes: ["*I:+name+ +description+"] },
			{ page_no: 1, page_size: 10 }
		)

		return res.data;
	}

	format_company_name(comp: Task.Company) {
		if (!comp) return "";
		return comp.name;
	}

	async update_details() {
		const user_id = this.user["user_id"];
		this.is_edit_form = user_id !== null && user_id !== undefined;
		this.is_sys_admin = await User.currentTypeIs(UT.SYSTEM_ADMIN);

		if (this.is_edit_form) {
			

			if (await User.currentTypeIs(
					UT.SYSTEM_ADMIN,
					UT.ADMIN,
					UT.HR_MANAGER
				)
			) {
				this.can_edit = true;
			}
		} else {
			this.can_edit = true;
		}

	}

	done() {
		this.result.emit(this.user);
	}

	cancel() {
		this.result.emit(null);
	}

	send_password_email() {
		if (this.is_edit_form) {
			this.show_password_email_button = false;
			User.send_password_email(this.user["user_id"]).then(() => {
				notification.show({
					icon: "email",
					title: language.translate("SUCCESS"),
					message: language.translate("USER.SEND_PASSWORD_EMAIL_SENT")
				});
			}).catch((_err) => {
				let err;

				try {
					err = JSON.parse(_err.response);
				} catch {
					err = null;
				}

				if (err && err.internal_error_code == 4106) {
					notification.show({
						icon: "warning",
						title: language.translate("FAILURE"),
						message: language.translate("USER.SEND_PASSWORD_EMAIL_NOT_SENT")
					});
				} else {
					notification.show({
						icon: "warning",
						title: language.translate("FAILURE"),
						message: language.translate("USER.SEND_PASSWORD_EMAIL_ERROR")
					});
				}
			});
		}
	}
}
